import type { ClientDto } from '@fos/shared/contracts/crm';
import { useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { toast } from '../../../ui/Toast';

/**
 * Archiwizacja klienta z potwierdzeniem (Etap 1: „archiwizować rekordy”). Historia, kontakty, projekty i zadania
 * zostają; klient znika z list i wyboru. Przywrócenie: ten sam dialog w trybie `restore`.
 */
export function ArchiveClientDialog({
  open,
  onOpenChange,
  client,
  mode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Pick<ClientDto, 'id' | 'name' | 'version'>;
  mode: 'archive' | 'restore';
}) {
  const utils = trpc.useUtils();
  const [error, setError] = useState<string | null>(null);
  const archive = trpc.clients.archive.useMutation();
  const restore = trpc.clients.restore.useMutation();
  const pending = archive.isPending || restore.isPending;

  async function confirm() {
    setError(null);
    try {
      if (mode === 'archive') await archive.mutateAsync({ id: client.id, version: client.version });
      else await restore.mutateAsync({ id: client.id, version: client.version });
      await utils.clients.invalidate();
      toast.success(mode === 'archive' ? `${client.name} w archiwum` : `Przywrócono ${client.name}`);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (pending) return;
        setError(null);
        onOpenChange(next);
      }}
      width="s"
      title={mode === 'archive' ? 'Przenieść klienta do archiwum?' : 'Przywrócić klienta?'}
      description={
        mode === 'archive'
          ? `${client.name} zniknie z listy klientów i z wyboru w zadaniach i czasie. Kontakty, projekty, zadania i historia zostają. Umowy utrzymania i koszty dostawców wstrzymujemy (bez przypomnień) do przywrócenia klienta.`
          : `${client.name} wróci na listę aktywnych klientów, a przypomnienia o płatnościach i odnowieniach wznowią się.`
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={confirm} loading={pending}>
            {mode === 'archive' ? 'Przenieś do archiwum' : 'Przywróć'}
          </Button>
        </>
      }
    >
      {error && <FormMessage>{error}</FormMessage>}
    </Dialog>
  );
}
